// เงินเดือน (Salary) helpers — the salary_em ledger + gen2 period math + export.
// Backend: Xano salary_em (table 63), proxied at /api/hr/salary*; gen2 + export
// at api:TzuPEHLK/em_salary/*. Pure: enums + money + date math, no I/O.
//
// salary_em row: one SIGNED line. `amount` > 0 = earning, < 0 = deduction; the
// FK column is `employee` (not employee_id). Net for an employee = Σ amount.

export { fmtMoney, fmtDateTH } from './employee.js'

/* ── the 21-value type enum (verbatim from the Xano schema) ────────────────── */
// เบี้ยเลี้ยงฝึกอบรม (added 2026-10-31): the ฿3,000 a new Counter / Admin / ผู้จัดการ
// earns once their first 10 shifts are worked. Typed by HR, never generated.
export const SALARY_TYPES = [
  'OT', 'หักสาย', 'เบี้ยขยัน', 'ลา', 'หักลาเกิน', 'ล้างห้องน้ำ', 'ค่าเดินทาง',
  'ค่าคอม', 'ทำโทษ', 'เงินกู้', 'เบิกล่วงหน้า', 'ค่าฝึกอบรม', 'ตอบ Facebook',
  'เงินเดือน', 'ค่าตำแหน่ง', 'ประกันสังคม', 'หัก กยศ', 'หักภาษี ณ ที่จ่าย',
  'อื่นๆ', 'ขอเข้าสาย', 'เบี้ยเลี้ยงฝึกอบรม',
]

// Types that are a DEDUCTION by nature — used only to preset the sign toggle in
// the manual-add form (the row's real sign is the sign of `amount`, always).
export const DEDUCTION_TYPES = new Set([
  'หักสาย', 'หักลาเกิน', 'ทำโทษ', 'เงินกู้', 'เบิกล่วงหน้า', 'ค่าฝึกอบรม',
  'ประกันสังคม', 'หัก กยศ', 'หักภาษี ณ ที่จ่าย',
])

// The types that can carry a recurring_uuid (link to Employee.recurring[]).
// Mirrors RECURRING_TYPES in lib/recurring.js — which is where a plan's type is
// chosen, so anything it offers can land here. `อื่นๆ` was missing until
// 2026-08-16 and it is the one gen2 actually writes most (live rows read
// "Handpiece Acteon [4/5]"), so every equipment installment went un-badged.
export const RECURRING_ROW_TYPES = new Set(['ค่าฝึกอบรม', 'เงินกู้', 'อื่นๆ'])

export const salaryTypeIsDeduction = (type) => DEDUCTION_TYPES.has(type)

// A materialised recurring installment: a recurring type WITH a non-empty uuid.
export function isRecurringRow(row) {
  return !!row && RECURRING_ROW_TYPES.has(row.type) && !!row.recurring_uuid
}

/* ── money ─────────────────────────────────────────────────────────────────── */
export const rowAmount = (r) => Number(r?.amount || 0)

// Net = Σ signed amount over a set of rows.
export function netOf(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((s, r) => s + rowAmount(r), 0)
}

// Split earnings / deductions totals (for the summary strip).
export function totals(rows) {
  let earn = 0, deduct = 0
  for (const r of Array.isArray(rows) ? rows : []) {
    const a = rowAmount(r)
    if (a >= 0) earn += a; else deduct += a
  }
  return { earn, deduct, net: earn + deduct }
}

// Combine a positive magnitude + a sign choice into the signed amount stored.
export function signedAmount(magnitude, isDeduction) {
  const m = Math.abs(Number(magnitude) || 0)
  return isDeduction ? -m : m
}

// "+1,234" / "−875" — signed, with an explicit leading + on earnings so the
// ledger reads as a running sheet. 0 → "0".
export function fmtSigned(amount) {
  const n = Number(amount) || 0
  const body = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2 })
  if (n > 0) return `+${body}`
  if (n < 0) return `−${body}` // U+2212 minus, matches the rest of the app
  return '0'
}

// Amount colour tone by sign.
export function amountTone(amount) {
  const n = Number(amount) || 0
  if (n > 0) return 'text-emerald-600'
  if (n < 0) return 'text-rose-600'
  return 'text-slate-400'
}

/* ── 26 → 25 payroll period (dup of clockin.js payrollPeriod — dedupe post-merge)
 * The Salary tab is month/year-oriented for the ledger, but GENERATION runs over
 * the same 26→25 window Clock-in uses. Kept local so this file does not couple to
 * the actively-changing clockin.js. ────────────────────────────────────────── */
const pad2 = (n) => String(n).padStart(2, '0')
const iso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
function parseISO(s) {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date()
}

// The payroll window whose payslip is being built right now.
export function payrollPeriod(now = new Date()) {
  const anchor = now.getDate() >= 26 ? 1 : 0 // past the 25th → the run that just opened
  const end = new Date(now.getFullYear(), now.getMonth() + anchor, 25)
  const start = new Date(end.getFullYear(), end.getMonth() - 1, 26)
  return { start: iso(start), end: iso(end) }
}

// Step the 26→25 window by whole periods.
export function shiftPeriod(period, delta) {
  const e = parseISO(period.end)
  const end = new Date(e.getFullYear(), e.getMonth() + delta, 25)
  const start = new Date(end.getFullYear(), end.getMonth() - 1, 26)
  return { start: iso(start), end: iso(end) }
}

// The DEFAULT 26→25 window that produces a given ledger month/year, i.e. the run
// ending on the 25th of that month. gen2 tags the written rows with this
// year/month (taken from the end date), so this is the period the Generate modal
// pre-fills when the user is viewing {year, month}.
export function periodForMonth(year, month) {
  const end = new Date(year, month - 1, 25)
  const start = new Date(end.getFullYear(), end.getMonth() - 1, 26)
  return { start: iso(start), end: iso(end) }
}

// {year, month} implied by a period's END date (gen2's rule).
export function ymFromEnd(endISO) {
  const d = parseISO(endISO)
  return { year: d.getFullYear(), month: d.getMonth() + 1 }
}

// Is this period exactly the 26→25 default for its own end-month? (drives the
// "you changed the period" second-confirm before generating).
export function isDefaultPeriod(period) {
  const { year, month } = ymFromEnd(period?.end)
  const def = periodForMonth(year, month)
  return def.start === period?.start && def.end === period?.end
}

/* ── grouping ──────────────────────────────────────────────────────────────── */
// Group ONE person's rows by month, newest first — the self-service view
// (/me/salary), where the employee is fixed and the months are the list.
// Within a month, earnings come before deductions and each side is ordered by
// magnitude, so เงินเดือน heads the slip and the small change follows.
// Returns [{ year, month, rows, earn, deduct, net }].
export function groupByMonth(rows) {
  const map = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    const year = Number(r?.year) || 0
    const month = Number(r?.month) || 0
    if (!year || !month) continue // a row with no period cannot be filed under one
    const key = year * 100 + month
    if (!map.has(key)) map.set(key, { year, month, rows: [] })
    map.get(key).rows.push(r)
  }
  return [...map.values()]
    .sort((a, b) => (b.year * 100 + b.month) - (a.year * 100 + a.month))
    .map((g) => {
      const rows = [...g.rows].sort((a, b) => {
        const sa = rowAmount(a) >= 0 ? 0 : 1
        const sb = rowAmount(b) >= 0 ? 0 : 1
        if (sa !== sb) return sa - sb
        return Math.abs(rowAmount(b)) - Math.abs(rowAmount(a))
      })
      return { ...g, rows, ...totals(rows) }
    })
}

// Group ledger rows by their `employee` FK, preserving arrival order (the list
// arrives sorted by employee). Returns [{ employee, rows, net }].
export function groupByEmployee(rows) {
  const order = []
  const map = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    const key = Number(r?.employee) || 0
    if (!map.has(key)) { map.set(key, []); order.push(key) }
    map.get(key).push(r)
  }
  return order.map((employee) => {
    const list = map.get(employee)
    // Calculated (generate:true) rows first, then manual (generate:false). Array
    // sort is stable, so the original order is preserved within each bucket.
    const rows = [...list].sort((a, b) => (b.generate ? 1 : 0) - (a.generate ? 1 : 0))
    return { employee, rows, net: netOf(rows) }
  })
}
